import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useBusinessTimezone } from "../../../context/BusinessTimezone/BusinessTimezoneContext";
import { formatMessageCalendarDay, formatMessageClock } from "../../../helpers/dateTimeFormat";
import { i18n } from "../../../translate/i18n";
import {
	formatConnectionChannelLabel,
	getChannelBadgeClass,
	getChannelIcon,
	getConnectionIntegrationType,
	getIntegrationBadgeClass,
	getIntegrationCardClass,
	getIntegrationCardFooterClass,
	HUB_CHANNEL_CARD,
	NATIVE_CHANNELS,
	SEAMLESS_CHANNELS,
} from "../connectionChannelsCatalog";
import useHubOAuth from "../hooks/useHubOAuth";

function IconRecommended() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="currentColor">
			<path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function MarketplaceChannelCard({ channel, onAdd, disabled = false, highlightMissingToken = false }) {
	return (
		<div
			className={`flex h-full min-h-[168px] flex-col rounded-lg border bg-card p-4 shadow-sm dark:bg-default-900 ${
				highlightMissingToken
					? "border-2 border-dashed border-amber-400/70 dark:border-amber-500/50"
					: channel.cardClass ||
						"border-default-200 dark:border-default-700"
			} ${disabled ? "opacity-60" : ""}`}
		>
			<div className="flex min-h-0 flex-1 flex-col gap-3">
				<div className="flex items-start gap-3">
					<div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-default-100 dark:bg-default-800">
						<img src={channel.icon} alt="" className="size-7 object-contain" />
					</div>
					<div className="min-w-0 flex-1">
						<h3 className="m-0 line-clamp-2 text-sm font-semibold leading-snug text-default-900 dark:text-default-50">
							{i18n.t(channel.labelKey)}
						</h3>
						{channel.recommended && (
							<span className="mt-1.5 inline-flex w-fit max-w-full items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
								<IconRecommended />
								<span className="truncate">{i18n.t("connections.picker.recommended")}</span>
							</span>
						)}
					</div>
				</div>

				<p className="mb-0 line-clamp-3 min-h-[3.75rem] text-[13px] leading-snug text-default-500 dark:text-default-400">
					{i18n.t(channel.descriptionKey)}
				</p>
			</div>

			<div className="mt-3 flex shrink-0 items-center justify-end border-t border-default-200 pt-3 dark:border-default-700">
				<button
					type="button"
					onClick={onAdd}
					disabled={disabled}
					className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${channel.buttonClass}`}
				>
					{i18n.t("connections.picker.addChannel")}
				</button>
			</div>
		</div>
	);
}

function SeamlessTokenWarning() {
	return (
		<TokenWarning
			titleKey="connections.picker.seamlessTokenWarning.title"
			descriptionKey="connections.picker.seamlessTokenWarning.description"
			pathKey="connections.picker.seamlessTokenWarning.path"
			linkKey="connections.picker.seamlessTokenWarning.link"
		/>
	);
}

function TokenWarning({ titleKey, descriptionKey, pathKey, linkKey }) {
	return (
		<div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 dark:border-amber-500/30 dark:bg-amber-500/10">
			<div className="flex items-start gap-3">
				<div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
					<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M12 9v4" />
						<path d="M12 17h.01" />
						<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
					</svg>
				</div>
				<div className="min-w-0">
					<p className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
						{i18n.t(titleKey)}
					</p>
					<p className="mb-2 text-sm leading-relaxed text-amber-800/90 dark:text-amber-100/80">
						{i18n.t(descriptionKey)}
					</p>
					<p className="mb-2 text-xs text-amber-800/80 dark:text-amber-100/70">
						{i18n.t(pathKey)}
					</p>
					<Link
						to="/Settings#hub-token"
						className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
					>
						{i18n.t(linkKey)}
						<svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M5 12h14" />
							<path d="m12 5 7 7-7 7" />
						</svg>
					</Link>
				</div>
			</div>
		</div>
	);
}

function IconQr() {
	return (
		<svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="3" y="3" width="7" height="7" rx="1" />
			<rect x="14" y="3" width="7" height="7" rx="1" />
			<rect x="3" y="14" width="7" height="7" rx="1" />
			<path d="M14 14h2v2h-2zM18 14h3v3h-3zM14 18h2v3h-2zM18 21h3" />
		</svg>
	);
}

function ConnectionActionButton({ onClick, children, variant = "default" }) {
	const variants = {
		qr: "border-emerald-500/35 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300",
		default: "border-default-200 bg-white text-default-700 hover:bg-default-50 dark:border-default-600 dark:bg-default-800 dark:text-default-200 dark:hover:bg-default-700",
		danger: "border-danger/25 bg-danger/5 text-danger hover:bg-danger/10",
	};

	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition ${variants[variant]}`}
		>
			{children}
		</button>
	);
}

function ConfiguredConnectionCard({
	connection,
	onEdit,
	onOpenQr,
	onNewQr,
	onDisconnect,
	onDelete,
	onWebhook,
}) {
	const { timeZone } = useBusinessTimezone();
	const icon = getChannelIcon(connection.channel);
	const channelLabel = formatConnectionChannelLabel(connection.channel, i18n.t.bind(i18n));
	const channelBadgeClass = getChannelBadgeClass(connection.channel);
	const integrationType = getConnectionIntegrationType(connection);
	const integrationBadgeClass = getIntegrationBadgeClass(integrationType);
	const integrationCardClass = getIntegrationCardClass(integrationType);
	const integrationFooterClass = getIntegrationCardFooterClass(integrationType);
	const createdAt = connection.createdAt || connection.updatedAt;
	const createdAtLabel = createdAt
		? `${formatMessageCalendarDay(createdAt, timeZone)} ${formatMessageClock(createdAt, timeZone)}`
		: null;

	return (
		<div
			className={`flex h-full flex-col rounded-lg border p-3.5 shadow-sm ${integrationCardClass}`}
		>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="shrink-0 text-xs font-medium text-default-400 dark:text-default-500">
						# {connection.id}
					</span>
					<h3 className="m-0 min-w-0 flex-1 truncate text-sm font-semibold leading-tight text-default-900 dark:text-default-50">
						{connection.name}
					</h3>
					<div className="flex shrink-0 items-center gap-0.5">
						<button
							type="button"
							className="btn-ghost !p-1.5 text-default-500 hover:text-default-800 dark:text-default-400 dark:hover:text-default-100"
							onClick={() => onEdit(connection)}
							title={i18n.t("connections.buttons.edit")}
							aria-label={i18n.t("connections.buttons.edit")}
						>
							<IconEdit />
						</button>
						<button
							type="button"
							className="btn-ghost !p-1.5 text-danger hover:bg-danger/10"
							onClick={() => onDelete(connection.id)}
							title={i18n.t("connections.buttons.delete")}
							aria-label={i18n.t("connections.buttons.delete")}
						>
							<IconDelete />
						</button>
					</div>
				</div>
				<div className="mt-2 flex items-center justify-between gap-3">
					<div className="flex min-w-0 flex-wrap items-center gap-2">
						{icon && (
							<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-white shadow-sm dark:bg-default-800">
								<img src={icon} alt="" className="size-7 object-contain" />
							</div>
						)}
						{connection.channel && channelLabel && (
							<span
								className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${channelBadgeClass}`}
							>
								{channelLabel}
							</span>
						)}
						<span
							className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${integrationBadgeClass}`}
						>
							{i18n.t(`connections.marketplace.integrationTypes.${integrationType}`)}
						</span>
					</div>

					<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
						{["whatsappcloud", "facebook"].includes(connection.channel) && (
							<ConnectionActionButton onClick={() => onWebhook(connection)}>
								{i18n.t("connections.buttons.reconnectWebhook")}
							</ConnectionActionButton>
						)}
						{connection.channel === "whatsapp" &&
							["OPENING", "qrcode"].includes(connection.status) && (
							<ConnectionActionButton variant="qr" onClick={() => onOpenQr(connection)}>
								<IconQr />
								{i18n.t("connections.buttons.qrcode")}
							</ConnectionActionButton>
						)}
						{connection.channel === "whatsapp" && connection.status === "DISCONNECTED" && (
							<ConnectionActionButton variant="qr" onClick={() => onNewQr(connection.id)}>
								<IconQr />
								{i18n.t("connections.buttons.newQr")}
							</ConnectionActionButton>
						)}
						{connection.channel === "whatsapp" &&
							["CONNECTED", "PAIRING", "TIMEOUT"].includes(connection.status) && (
							<ConnectionActionButton variant="danger" onClick={() => onDisconnect(connection.id)}>
								{i18n.t("connections.buttons.disconnect")}
							</ConnectionActionButton>
						)}
					</div>
				</div>
			</div>
			{(connection.isDefault || createdAtLabel) && (
				<div
					className={`mt-2.5 flex flex-wrap items-center gap-2 border-t pt-2.5 ${integrationFooterClass} ${
						connection.isDefault ? "justify-between" : "justify-end"
					}`}
				>
					{connection.isDefault && (
						<span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
							{i18n.t("connections.table.default")}
						</span>
					)}
					{createdAtLabel && (
						<p className="mb-0 text-xs text-default-500 dark:text-default-400">
							{i18n.t("connections.marketplace.createdAt")} {createdAtLabel}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function statusMessageKey(status) {
	if (status === "success") return "connections.picker.hubConnectSuccess";
	if (status === "popupBlocked") return "connections.picker.hubPopupBlocked";
	if (status === "missingCompany") return "connections.picker.hubMissingCompany";
	if (status === "error") return "connections.picker.hubConnectError";
	return "";
}

const MARKETPLACE_GRID =
	"grid grid-cols-1 items-stretch gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

export default function ConnectionChannelsMarketplace({
	connections,
	loading,
	onSelectNativeChannel,
	onConfigureHubChannel,
	onEditConnection,
	onOpenQr,
	onNewQr,
	onWebhook,
	onDisconnect,
	onDelete,
}) {
	const [searchParam, setSearchParam] = useState("");
	const { companyUuid, hubTokenLoading, status, success, errorMessage, openMetaPopup, resetStatus } = useHubOAuth({
		enabled: true,
	});
	const hubTokenMissing = !hubTokenLoading && !companyUuid;

	useEffect(() => {
		if (status !== "success") return;
		onConfigureHubChannel("seamless");
		resetStatus();
	}, [status, onConfigureHubChannel, resetStatus]);

	const filteredConnections = useMemo(() => {
		const term = searchParam.toLowerCase().trim();
		if (!term) return connections || [];

		return (connections || []).filter((connection) =>
			[connection.id, connection.name, connection.channel, connection.status]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(term)
		);
	}, [connections, searchParam]);

	const statusKey = statusMessageKey(status);
	const showWhatsappHubHelp =
		status === "error" && /whatsapp/i.test(errorMessage || "");
	const hasConnections = (connections || []).length > 0;

	return (
		<div className="space-y-7">
			{(loading || hasConnections) && (
				<section>
					<div className="mb-3.5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
						<div>
							<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
								{i18n.t("connections.marketplace.configuredTitle")}
							</h3>
							<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
								{i18n.t("connections.marketplace.configuredDescription")}
							</p>
						</div>
						{hasConnections && (
							<div className="relative w-full sm:max-w-xs">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-9"
									placeholder={i18n.t("connections.searchPlaceholder")}
									value={searchParam}
									onChange={(e) => setSearchParam(e.target.value)}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<circle cx="11" cy="11" r="8" />
										<path d="m21 21-4.35-4.35" />
									</svg>
								</div>
							</div>
						)}
					</div>
					{loading ? (
						<div className={`${MARKETPLACE_GRID} animate-pulse`}>
							{[1, 2, 3].map((item) => (
								<div
									key={item}
									className="min-h-[168px] rounded-lg border border-default-200 bg-default-100 dark:border-default-700 dark:bg-default-800"
								/>
							))}
						</div>
					) : filteredConnections.length === 0 ? (
						<div className="rounded-lg border border-dashed border-default-200 px-4 py-8 text-center dark:border-default-700">
							<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
								{i18n.t("connections.empty")}
							</p>
							{searchParam && (
								<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
									{i18n.t("connections.emptyHint")}
								</p>
							)}
						</div>
					) : (
						<div className={MARKETPLACE_GRID}>
							{filteredConnections.map((connection) => (
								<ConfiguredConnectionCard
									key={connection.id}
									connection={connection}
									onEdit={onEditConnection}
									onOpenQr={onOpenQr}
									onNewQr={onNewQr}
									onWebhook={onWebhook}
									onDisconnect={onDisconnect}
									onDelete={onDelete}
								/>
							))}
						</div>
					)}
				</section>
			)}

			<section>
				<div className="mb-3.5">
					<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
						{i18n.t("connections.picker.sections.native.title")}
					</h3>
					<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
						{i18n.t("connections.picker.sections.native.description")}
					</p>
				</div>
				<div className={MARKETPLACE_GRID}>
					{NATIVE_CHANNELS.map((channel) => (
						<MarketplaceChannelCard
							key={channel.id}
							channel={channel}
							onAdd={() => onSelectNativeChannel(channel.id)}
						/>
					))}
					<MarketplaceChannelCard
						channel={HUB_CHANNEL_CARD}
						onAdd={() => onConfigureHubChannel("hub")}
						highlightMissingToken={hubTokenMissing}
					/>
				</div>
			</section>

			<section>
				<div className="mb-3.5">
					<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
						{i18n.t("connections.picker.sections.seamless.title")}
					</h3>
					<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
						{i18n.t("connections.picker.sections.seamless.description")}
					</p>
				</div>
				{hubTokenMissing && <SeamlessTokenWarning />}
				<div className={MARKETPLACE_GRID}>
					{SEAMLESS_CHANNELS.map((channel) => (
						<MarketplaceChannelCard
							key={channel.id}
							channel={channel}
							onAdd={() => openMetaPopup(channel)}
							disabled={hubTokenMissing}
						/>
					))}
				</div>
			</section>

			{statusKey && (
				<div
					className={`rounded-lg px-4 py-3 text-center text-sm ${
						success ? "bg-success/10 text-success font-semibold" : "bg-danger/10 text-danger"
					}`}
				>
					<p className="m-0 font-semibold">{i18n.t(statusKey)}</p>
					{!success && errorMessage && (
						<p className="mb-0 mt-2 font-normal">{errorMessage}</p>
					)}
					{!success && showWhatsappHubHelp && (
						<p className="mb-0 mt-2 font-normal text-default-600 dark:text-default-300">
							{i18n.t("connections.picker.hubWhatsappNotEnabled.help")}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
